import { DashboardMenu } from "../data/menu";
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import DashboardInfo from "./Dashboard/DashboardInfo";

export default function Dashboard() {
  const { t } = useTranslation();
  const isRTL = t("dir") === "rtl";

  const getGradientColor = (color: string) => {
    const colorMap: { [key: string]: string } = {
      'blue': 'from-blue-500 to-blue-600',
      '#CCD5AE': 'from-green-400 to-green-500',
      '#E0E5B6': 'from-yellow-400 to-yellow-500',
      '#E8B86D': 'from-orange-400 to-orange-500',
      '#CCD5EE': 'from-indigo-400 to-indigo-500',
      '#FAEDCE': 'from-amber-300 to-amber-400',
      '#CEDF9F': 'from-lime-400 to-lime-500',
      'purple': 'from-purple-500 to-purple-600',
      'pink': 'from-pink-500 to-pink-600',
      'indigo': 'from-indigo-500 to-indigo-600',
      'cyan': 'from-cyan-500 to-cyan-600',
      'orange': 'from-orange-500 to-orange-600',
    };
    return colorMap[color] || 'from-gray-500 to-gray-600';
  };

  const visibleItems = DashboardMenu.filter(item => item.show);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 md:p-6" dir={t("dir")}>
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 md:p-8 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">
                    {t('Dashboard')}
                  </h1>
                  <p className="text-blue-100 text-sm md:text-base">
                    {t('Welcome to your control panel')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Menu Grid */}
        <nav aria-label={t('Dashboard Menu')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visibleItems.map((item: any, index: number) => (
              <motion.div
                key={item.id || item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Link
                  to={item.url}
                  className="block h-full"
                >
                  <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group-hover:border-blue-200 h-full flex flex-col">
                    {/* Icon Header with Gradient */}
                    <div className={`bg-gradient-to-br ${getGradientColor(item.color || 'blue')} p-6 relative overflow-hidden`}>
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                          <img
                            src={item.icon}
                            alt=""
                            className="w-8 h-8 filter brightness-0 invert"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg 
                            className={`w-6 h-6 text-white ${isRTL ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 flex-1 flex flex-col">
                      <h2 className="text-lg font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                        {t(item.name)}
                      </h2>

                      {item.sub && item.sub.length > 0 && (
                        <div className="mt-auto">
                          <div className="space-y-2">
                            {item.sub.slice(0, 3).map((subItem: any, subIndex: number) => (
                              <Link
                                key={subItem.id || subItem.name}
                                to={subItem.url}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors group/sub"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover/sub:bg-blue-500 transition-colors"></div>
                                <span className="truncate">{t(subItem.name)}</span>
                              </Link>
                            ))}
                            {item.sub.length > 3 && (
                              <div className="text-xs text-gray-400 pt-1">
                                +{item.sub.length - 3} {t('more')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </nav>

        {/* Quick Stats or Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-600">
              {t('Total modules')}: <span className="font-semibold text-gray-800">{visibleItems.length}</span>
            </p>
          </div>
        </motion.div>
        <motion.div>
        <DashboardInfo></DashboardInfo>
        </motion.div>
      </div>
    </div>
  );
}